import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { useQuery, useLazyQuery, useMutation } from "../../../GraphQL/helpers";
import { Row, Column } from "../../global/grid";
import Button from "../../global/button";
import Modal, { ModalBody, ModalFooter } from "../../global/modal";
import Alert from "../../global/alert";
import Form, { FormInput, FormSelect, FormCheckbox, FormTextArea } from "../../global/forms";
import Validator from "../../../utilities/validators";
import { GET_COUNTRIES, GET_STATES, GET_CITIES, GET_DISTRICTS } from "../../../GraphQL/queries/fetchAddressInfo";
import GET_PROFILE from "../../../GraphQL/queries/fetchProfile";
import { ADD_ADDRESS, UPDATE_ADDRESS } from "../../../GraphQL/mutations/address";
import errorHandler from "../../../utilities/errorHandler";

const AddressModal = ({
    address,
    isNewAddress,
    onClose,
    onOpened,
    innerRef,
    ...attributes
}) => {
    const { t } = useTranslation("components.profile.addressModal");
    const validatorsLocale = t("utilities.validators:validators", { returnObjects: true });
    const validator = new Validator(validatorsLocale);
    const { addToast } = useToasts();
    const formHook = useForm();
    const { handleSubmit, formState, setValue, watch } = formHook;
    const { isSubmitting } = formState;
    const { data: countriesData } = useQuery(GET_COUNTRIES);
    const [getStatesData] = useLazyQuery(GET_STATES);
    const [getCitiesData] = useLazyQuery(GET_CITIES);
    const [getDistrictsData] = useLazyQuery(GET_DISTRICTS);
    const [addAddress, { loading: addLoading }] = useMutation(ADD_ADDRESS);
    const [updateAddress, { loading: updateLoading }] = useMutation(UPDATE_ADDRESS);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [modalError, setModalError] = useState(null);
    const [initializedEdit, setInitializedEdit] = useState(true);
    const [loadedDistricts, setLoadedDistricts] = useState(false);
    const [loading, setLoading] = useState(false);
    let modalRef = useRef(null);
    if (innerRef) {
        modalRef = innerRef;
    }
    const currentCountryId = watch("country", "-1");
    const currentStateId = watch("state", "-1");
    const currentCityId = watch("city", "-1");

    const getStates = async (countryId) => {
        setValue("state", "-1");
        setValue("city", "-1");
        setValue("address2", "-1");
        const { data, error } = await getStatesData({ countryId: parseInt(countryId, 10) });
        if (!error && data) {
            setStates(data.departamentos);
        }
    };

    const getCities = async (stateId) => {
        setValue("city", "-1");
        setValue("address2", "-1");
        const { data, error } = await getCitiesData({ stateId: parseInt(stateId, 10) });
        if (!error && data) {
            setCities(data.ciudades);
        }
    };

    const getDistricts = async (cityId) => {
        setValue("address2", "-1");
        const { data, error } = await getDistrictsData({ cityId: parseInt(cityId, 10) });
        if (!error && data) {
            setDistricts(data.distritos);
        }
    };

    useEffect(() => {
        if (!isNewAddress && address && !initializedEdit && loadedDistricts) {
            /* TODO: Find a better way to do this */
            setTimeout(() => {
                setValue("state", address.departamentoId);
                setTimeout(() => {
                    setValue("city", address.ciudadId);
                    setTimeout(() => {
                        setValue("address2", address.distritoId || -1);
                    }, 0);
                }, 0);
            }, 0);
            setInitializedEdit(true);
        }
    }, [initializedEdit, loadedDistricts]); // eslint-disable-line

    // watch change in country
    useEffect(() => {
        if (currentCountryId > 0) {
            (async () => {
                await getStates(currentCountryId);
            })();
        }
    }, [currentCountryId]); // eslint-disable-line

    // watch change in state
    useEffect(() => {
        if (currentStateId > 0) {
            (async () => {
                await getCities(currentStateId);
            })();
        }
    }, [currentStateId]); // eslint-disable-line

    // watch change in city
    useEffect(() => {
        if (currentCityId > 0) {
            (async () => {
                await getDistricts(currentCityId);
                setLoadedDistricts(true);
            })();
        }
    }, [currentCityId]); // eslint-disable-line

    useEffect(() => {
        setLoading(addLoading || updateLoading);
    }, [addLoading, updateLoading]);

    const onOpenedHandler = () => {
        if (!isNewAddress && address) {
            setInitializedEdit(false);
        }

        onOpened && onOpened();
    };

    const onCloseHandler = () => {
        setModalError(null);
        setLoadedDistricts(false);
        onClose && onClose();
    };

    const onSubmit = async (data) => {
        let result;

        try {
            const country = countriesData.paises.find(c => c.id === currentCountryId.toString());
            const state = states.find(s => s.id === currentStateId.toString());
            const city = cities.find(c => c.id === currentCityId.toString())?.nombre ?? "";
            const district = data.address2 ? districts.find(d => d.id === data.address2.toString()).nombre : null;
            const districtId = data.address2 ? parseInt(data.address2, 10) : null;
            const cityId = parseInt(data.city, 10);
            const stateId = parseInt(state.id, 10);
            const countryId = parseInt(country.id, 10);

            if (isNewAddress) {
                result = addAddress({
                    variables: {
                        address1: data.address1,
                        districtId,
                        cityId,
                        reference: data.reference,
                        addressType: country.nombreEspanol === "Perú" ? "ENVIO" : "FACTURACION",
                        principal: data.primary,
                    },
                    optimisticResponse: {
                        agregarDireccion: {
                            direccion: {
                                id: 0,
                                direccion: data.address1,
                                distritoId: districtId,
                                distrito: district,
                                ciudadId: cityId,
                                ciudad: city,
                                departamentoId: stateId,
                                departamento: state.nombre,
                                paisId: countryId,
                                pais: country.nombreEspanol,
                                referencia: data.reference,
                                principal: data.primary,
                                __typename: "Direccion",
                            },
                            errores: null,
                        }
                    },
                    update(cache, { data: { agregarDireccion } }) {
                        const { perfil } = cache.readQuery({ query: GET_PROFILE });

                        if (agregarDireccion?.direccion) {
                            cache.writeQuery({
                                query: GET_PROFILE,
                                data: {
                                    perfil: {
                                        ...perfil,
                                        direcciones: [...perfil.direcciones, agregarDireccion.direccion],
                                    }
                                },
                            });

                            if (agregarDireccion.direccion.principal && perfil.direcciones.length) {
                                const currentMainAddress = perfil.direcciones.find(d => d.principal && d.id !== agregarDireccion.direccion.id);

                                if (currentMainAddress.id !== agregarDireccion.direccion.id) {
                                    cache.modify({
                                        id: cache.identify(currentMainAddress),
                                        fields: { principal: () => false },
                                    });
                                }
                            }
                        }
                    },
                });
            } else {
                result = updateAddress({
                    variables: {
                        id: address.id,
                        address1: data.address1,
                        districtId,
                        cityId,
                        reference: data.reference,
                        principal: data.primary,
                    },
                    optimisticResponse: {
                        editarDireccion: {
                            direccion: {
                                id: address.id,
                                direccion: data.address1,
                                distritoId: districtId,
                                distrito: district,
                                ciudadId: cityId,
                                ciudad: city,
                                departamentoId: stateId,
                                departamento: state.nombre,
                                paisId: countryId,
                                pais: country.nombreEspanol,
                                referencia: data.reference,
                                principal: data.primary,
                                __typename: "Direccion",
                            },
                            errores: null,
                        }
                    },
                    update(cache, { data: { editarDireccion } }) {
                        if (editarDireccion?.direccion?.principal ?? false) {
                            const { perfil } = cache.readQuery({ query: GET_PROFILE });
                            const currentMainAddress = perfil.direcciones.find(d => d.principal && d.id !== editarDireccion.direccion.id);

                            if (currentMainAddress.id !== editarDireccion.direccion.id) {
                                cache.modify({
                                    id: cache.identify(currentMainAddress),
                                    fields: { principal: () => false },
                                });
                            }
                        }
                    },
                });
            }

            const { data: resultData } = await result;
            const { errores } = isNewAddress ? resultData.agregarDireccion : resultData.editarDireccion;

            if (errores) {
                setModalError({
                    title: t(`errors.${isNewAddress ? "create" : "update"}.default.title`),
                    message: errores[0].mensaje,
                });
            } else {
                addToast((
                    <div>
                        <div>
                            <strong>{t(isNewAddress ? "addConfirm" : "updateConfirm")}</strong>
                        </div>
                        <div>{data.address1}</div>
                        {data.address2 && <div>{data.address2}</div>}
                        <div>{city}, {state.nombre}</div>
                        <div>{country.nombreEspanol}</div>
                    </div>
                ), {
                    appearance: "success",
                });
                modalRef?.current && modalRef.current.close();
            }
        } catch(error) {
            errorHandler(error, () => {
                setModalError(t(
                  `errors.${isNewAddress ? "create" : "update"}.default`,
                  { returnObjects: true }
                ));
            });
        }
    };

    return (
        <Modal
            {...attributes}
            title={isNewAddress ? t("addAddress") : t("editAddress")}
            onOpened={onOpenedHandler}
            onClose={onCloseHandler}
            ref={modalRef}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    {modalError && (
                        <Row mb={5}>
                            <Column sm="12">
                                <Alert variant="danger" {...modalError}/>
                            </Column>
                        </Row>
                    )}

                    <Row>
                        <Column sm="12">
                            <Alert variant="info" message={t(`changeWarningMsg.${isNewAddress ? "add" : "edit"}`)}/>
                        </Column>
                    </Row>

                    <Row>
                        <Column sm="12" md="6">
                            <FormSelect
                              {...formHook}
                              validationRules={validator.addressCountry}
                              name="country"
                              options={countriesData?.paises ?? []}
                              valueProp="id"
                              textProp="nombreEspanol"
                              label={t("country.label")}
                              placeholder={t("country.placeholder")}
                              defaultValue={!isNewAddress && address ? address.paisId : -1}
                            />
                        </Column>
                        <Column sm="12" md="6">
                            <FormSelect
                              {...formHook}
                              validationRules={validator.addressState}
                              name="state"
                              options={states}
                              valueProp="id"
                              textProp="nombre"
                              label={t("state.label")}
                              placeholder={t("state.placeholder")}
                              defaultValue={!isNewAddress && address ? address.departamentoId : -1}
                              disabled={!states.length || currentCountryId === "-1"}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column sm="12" md="6">
                            <FormSelect
                              {...formHook}
                              validationRules={validator.addressCity}
                              name="city"
                              options={cities}
                              valueProp="id"
                              textProp="nombre"
                              label={t("city.label")}
                              placeholder={t("city.placeholder")}
                              defaultValue={!isNewAddress && address ? address.ciudadId : -1}
                              disabled={!cities.length || currentStateId === "-1"}
                            />
                        </Column>
                        <Column sm="12" md="6">
                            <FormSelect
                              {...formHook}
                              name="address2"
                              options={districts}
                              valueProp="id"
                              textProp="nombre"
                              label={t("address2.label")}
                              placeholder={t("address2.placeholder")}
                              defaultValue={!isNewAddress && address ? address.distritoId || -1 : -1}
                              disabled={!districts.length || currentCityId === "-1"}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column sm="12">
                            <FormInput
                              {...formHook}
                              validationRules={validator.address1}
                              name="address1"
                              label={t("address1.label")}
                              placeholder={t("address1.placeholder")}
                              defaultValue={!isNewAddress && address ? address.direccion : ""}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column sm="12">
                            <FormTextArea
                              {...formHook}
                              name="reference"
                              label={t("reference.label")}
                              defaultValue={!isNewAddress && address ? address.referencia : ""}
                              rows={3}
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column sm="12">
                            <FormCheckbox
                              {...formHook}
                              name="primary"
                              label={t("makePrimary")}
                              inline
                              defaultChecked={address?.principal ?? false}
                              disabled={address.principal}
                            />
                        </Column>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    {/*<Button type="button" block size="md" colors="light" onClick={() => toggleModal(false)}>{t("close")}</Button>*/}
                    <Button type="submit" block size="lg" colors="primaryWithBg" disabled={isSubmitting || loading} loading={isSubmitting}>{t("save")}</Button>
                </ModalFooter>
            </Form>

        </Modal>
    );
};

export default AddressModal;
